import React from 'react';
import styled from 'styled-components';
import { ModuleType } from '..';
import { Type } from '@spotify-internal/encore-web';
import {
  body1FontWeight,
  display1FontSize,
  display1FontWeight,
  heading4FontSize,
  heading3LineHeight,
  screenSmMax,
  spacer4,
  spacer16,
  spacer20,
  spacer32,
  spacer40,
  spacer8,
  spacer80,
  spacer56,
  spacer48,
} from '@spotify-internal/encore-foundation';
import { articleMarkdown } from '../../Markdown';
import { ShareButtonsComponent } from '../../ui/ShareButtons';
import { hexToRgba } from '@mrkt/features/color-conversion';
import { ImageAspectBox } from '../../ui';

const maxWidth = '680px';

const Article = styled.div`
  padding: ${spacer40};
  display: flex;
  flex-direction: column;

  @media (max-width: ${screenSmMax}) {
    padding-left: ${spacer16};
    padding-right: ${spacer16};
    padding-bottom: ${spacer16};
  }
`;

const IntroductionWrapper = styled.div`
  padding-top: ${spacer16};
  padding-bottom: ${spacer56};
  max-width: ${maxWidth};
  margin: auto;
`;

const Introduction = styled(Type).attrs({
  variant: Type.heading2,
})`
  padding-top: ${spacer8};
`;

const BodyWrapper = styled.div``;

const dropCapStyle = `
  padding-left: ${spacer4};
  padding-bottom: 0px;
  padding-right: ${spacer8};
  float: left;
  position: relative;
  top: ${spacer4};
  margin-bottom: ${spacer4};
  margin-left: ${spacer8};
  margin-right: ${spacer4};
  font-size: ${display1FontSize};
  line-height: ${spacer80};
  font-weight: ${display1FontWeight};
`;

export const articleTextStyle = `
  font-size: ${heading4FontSize};
  font-weight: ${body1FontWeight};
  line-height: ${heading3LineHeight};
`;

export const DropCap = styled.p`
  max-width: ${maxWidth};
  margin: auto;
  &:first-letter {
    ${dropCapStyle}
  }
`;

const Body = styled.div`
  padding-bottom: ${spacer40};

  @media (max-width: ${screenSmMax}) {
    padding-bottom: ${spacer16};
  }

  figcaption {
    text-align: left;
  }

  a {
    text-decoration: underline;
  }

  ul,
  ol {
    max-width: ${maxWidth};
    margin: auto;
    padding-left: ${spacer48};
  }

  ol > li,
  ul > li {
    ${articleTextStyle}
    display: revert;
  }

  p {
    ${articleTextStyle}
    max-width: ${maxWidth};
    margin: auto;
  }

  h1,
  h2,
  h3,
  h4 {
    margin: auto;
    max-width: ${maxWidth};
  }

  iframe {
    display: block;
    margin: auto;
    max-width: ${maxWidth};
    padding-bottom: ${spacer32};
  }

  ${ImageAspectBox} {
    max-width: ${maxWidth};
    margin: auto;

    iframe {
      padding-bottom: 0;
    }
  }

  img {
    width: 100%;
  }
  div > iframe {
    padding-bottom: ${spacer56};
  }
`;

const ArticleDetails = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-top: ${spacer40};

  @media (max-width: ${screenSmMax}) {
    padding-top: ${spacer20};
    padding-bottom: 0;
  }
`;

const BottomSection = styled.div`
  max-width: ${maxWidth};
  display: flex;
  flex-direction: column;
  margin: auto;
  padding-top: ${spacer56};
  border-top: 1px solid ${({ theme }) => theme.box.text};

  & > p {
    font-size: ${heading4FontSize};
  }

  @media (max-width: ${screenSmMax}) {
    padding-top: ${spacer20};
    padding-bottom: 0;
  }
`;

const ShareButtonContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > div {
    padding-bottom: 0;

    svg {
      color: ${({ theme }) => theme.box.text};
      &:hover {
        color: ${({ theme }) => hexToRgba(theme.box.text, 0.5)};
      }
    }
  }
`;

const AuthorDate = styled(Type).attrs({
  variant: Type.body2,
})`
  @media (max-width: ${screenSmMax}) {
    display: none;
  }
`;

export type ArticleModuleType = ModuleType & {
  introduction?: string;
  body?: string;
  authorDateString?: string;
  initialDropcap?: boolean;
};

export const ArticleModule: React.FC<ArticleModuleType> = ({
  introduction = '',
  body = '',
  authorDateString = '',
  initialDropcap = false,
}) => {
  const markDown = articleMarkdown(body, initialDropcap);

  return (
    <Article>
      <IntroductionWrapper>
        <Introduction>{introduction}</Introduction>
      </IntroductionWrapper>
      <BodyWrapper>
        <Body
          dangerouslySetInnerHTML={{
            __html: markDown,
          }}
        />
      </BodyWrapper>
      <BottomSection>
        <p>
          Spotify for Artists helps you to develop the fanbase you need to reach
          your goals.
        </p>
        <ArticleDetails>
          <AuthorDate>{authorDateString}</AuthorDate>
          <ShareButtonContainer>
            Share this story
            <ShareButtonsComponent />
          </ShareButtonContainer>
        </ArticleDetails>
      </BottomSection>
    </Article>
  );
};
